import { useState, type FormEvent } from "react";
import { Copy, MoreHorizontal, Trash2 } from "lucide-react";
import { ActionModal, Avatar, Button, Card, IconButton, Picker, Row, Rows, Tag } from "@cntrl/ui";
import { useBandContext } from "../../app/bandContext.js";
import { useMe } from "../../app/hooks/useSession.js";
import {
  usePartnerLinks,
  useInvitePartner,
  useRemovePartnerLink,
  useCancelPartnerInvite,
  useSetPartnerRelationship,
  type PartnerLinkView,
} from "../../app/hooks/usePartnerLinks.js";
import { PARTNER_RELATIONSHIPS, partnerRelationshipLabel } from "../../app/partnerRelationship.js";
import { useReissueInviteLink } from "../../app/hooks/useMyInvites.js";
import { pushToast } from "../../app/toasts.js";
import { problemType } from "../../app/hooks/useRelease.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { nameInitials } from "../../app/hooks/inboxFormat.js";
import styles from "./PartnerBox.module.scss";
import { usePhoneLayout } from "../../app/phoneLayout.js";

export interface PartnerBoxProps {
  /** `box` — the mobile Contacts card, no heading (ART-222). `card` — a titled card in the desktop Contacts
   *  grid (ART-239), where every surface carries its own header. */
  variant?: "box" | "card";
}

/**
 * The PARTNER box — the member side of the partner view (ART-222/239; the UI says "partner", the code keeps
 * "partner"). The link is the member's alone: these are always the caller's own link and invite. Four states:
 *   • connected — the accepted link: the partner's picture + name · × (remove, behind a confirm). No status word: the row
 *                 existing IS the connection (product owner, 2026-10-08).
 *   • invited   — the pending invite: the email · Invited, with Copy link (re-issues: only the token's hash is
 *                 stored, so the original can never be shown again) and Cancel.
 *   • sent      — straight after inviting: the /join link to copy or share (it was emailed too).
 *   • blank     — "+ Add your partner" → email + the name to put on guest lists.
 * Hidden while the read is in flight or failed, so it never flashes "+ Add" over an existing partner.
 */
export function PartnerBox({ variant = "box" }: PartnerBoxProps) {
  const me = useMe();
  const q = usePartnerLinks();
  // An ARTIST account is the band itself and has no partner of its own (product owner, 2026-10-08) — the
  // `core.is_artist_account` rule: an active OWNER seat anywhere. The server refuses its invite too (422, + 911).
  const isArtistAccount = me.data?.seats?.some((s) => s.kind === "band" && s.isOwner) ?? false;
  if (isArtistAccount || q.isLoading || q.isError) return null;

  const body = <PartnerBody />;
  return variant === "card" ? (
    // Flush, like Friends and Crew: the shared Row insets itself to the header, so the rows line up with theirs.
    <Card title="Partner" flush>
      <div className={styles.cardBody}>{body}</div>
    </Card>
  ) : (
    <section className={styles.partner} aria-label="Partner">
      <div className={styles.card}>{body}</div>
    </section>
  );
}

function PartnerBody() {
  const { bandId } = useBandContext();
  const q = usePartnerLinks();
  const invite = useInvitePartner();
  const cancel = useCancelPartnerInvite();
  const reissue = useReissueInviteLink();
  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState("");
  const [alias, setAlias] = useState("");
  const [relationship, setRelationship] = useState("");
  const [managing, setManaging] = useState(false);
  // The address the server refused because it is already another member's partner (409 partner-already-linked) —
  // said in a modal rather than the form's error line: it is not a typo to fix, it is somebody else's partner.
  const [alreadyLinked, setAlreadyLinked] = useState<string | null>(null);
  // On a phone the worded button crowds the name and its Invited chip, so it becomes the copy icon.
  const wide = !usePhoneLayout();

  const link = q.data?.links?.[0];
  const pending = q.data?.pendingInvites?.[0];

  function submit(e: FormEvent) {
    e.preventDefault();
    const addr = email.trim();
    if (!addr) return;
    invite.mutate(
      { email: addr, alias: alias.trim() || undefined, relationship: relationship || undefined },
      {
        onSuccess: () => {
          setAdding(false);
          setEmail("");
          setAlias("");
          setRelationship("");
          // It was emailed; the invited row below now carries Copy Invite for sending it any other way.
          pushToast("Invite sent", "success");
        },
        onError: (err) => {
          if (problemType(err) === "partner-already-linked") setAlreadyLinked(addr);
        },
      },
    );
  }

  // Copy Invite: only the link's HASH is stored, so the original can never be shown again — this re-issues a
  // fresh link (the old one stops working) and puts it on the clipboard.
  function copyInvite(inviteId: string) {
    reissue.mutate(
      { kind: "partner", bandId, inviteId },
      {
        onSuccess: async (l) => {
          try {
            if (!l.joinUrl) throw new Error("no link");
            await navigator.clipboard.writeText(l.joinUrl);
            pushToast("Invite Copied", "success");
          } catch {
            pushToast("Couldn’t copy the invite link. Try again.");
          }
        },
        onError: () => pushToast("Couldn’t copy the invite link. Try again."),
      },
    );
  }

  // The same row as every other people card here (the shared Row): picture, name, what kind of partner, and a (…)
  // that opens the one place to change that or remove them.
  if (link) {
    const name = link.partnerName || link.partnerEmail || `Your ${partnerRelationshipLabel(link.relationship)?.toLowerCase() ?? "partner"}`;
    return (
      <>
        <Rows>
          <Row
            avatar={<PersonAvatar personId={link.partnerPersonId} avatarMediaId={link.partnerAvatarMediaId} initials={nameInitials(name)} />}
            name={name}
            sub={partnerRelationshipLabel(link.relationship) ?? undefined}
            trailing={
              <IconButton icon={MoreHorizontal} label={`Manage ${name}`} size="sm" onClick={() => setManaging(true)} />
            }
          />
        </Rows>
        {managing && <ManagePartnerModal link={link} name={name} onClose={() => setManaging(false)} />}
      </>
    );
  }

  if (pending) {
    const name = pending.alias || pending.email || `Invited ${partnerRelationshipLabel(pending.relationship)?.toLowerCase() ?? "partner"}`;
    return (
      <Rows>
        <Row
          avatar={<Avatar initials={nameInitials(name)} />}
          name={name}
          sub={
            <span className={styles.invitedSub}>
              {partnerRelationshipLabel(pending.relationship) && (
                <span>{partnerRelationshipLabel(pending.relationship)}</span>
              )}
              <Tag size="sm" tone="warning">Invited</Tag>
            </span>
          }
          pending
          trailing={
            <div className={styles.rowActions}>
              {wide ? (
                <Button
                  size="sm"
                  variant="ghost"
                  loading={reissue.isPending}
                  onClick={() => pending.inviteId && copyInvite(pending.inviteId)}
                >
                  Copy Invite
                </Button>
              ) : (
                <IconButton
                  icon={Copy}
                  label="Copy Invite"
                  size="sm"
                  disabled={reissue.isPending}
                  onClick={() => pending.inviteId && copyInvite(pending.inviteId)}
                />
              )}
              <IconButton
                icon={Trash2}
                label={`Cancel the invite to ${name}`}
                tone="danger"
                size="sm"
                disabled={cancel.isPending}
                onClick={() =>
                  pending.inviteId &&
                  cancel.mutate(pending.inviteId, { onError: () => pushToast("Couldn’t cancel that invite. Try again.") })
                }
              />
            </div>
          }
        />
      </Rows>
    );
  }

  if (adding) {
    return (
      <>
        <form className={`${styles.inset} ${styles.form}`} onSubmit={submit}>
          <input
            className={styles.email}
            type="email"
            inputMode="email"
            autoComplete="off"
            placeholder="Their email"
            aria-label="Partner's email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- opens on an explicit tap; focusing its first field is the expected next action, not a steal
            autoFocus
          />
          <input
            className={styles.email}
            type="text"
            autoComplete="off"
            maxLength={80}
            placeholder="Name on the guest list (e.g. Sarah W.)"
            aria-label="Name on the guest list"
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
          />
          <Picker
            label="Relationship"
            hideLabel
            placeholder="Relationship (optional)"
            triggerClassName={styles.relationshipTrigger}
            value={relationship}
            onChange={setRelationship}
            options={PARTNER_RELATIONSHIPS.map((r) => ({ value: r.value, label: r.label }))}
          />
          {invite.isError && problemType(invite.error) !== "partner-already-linked" && (
            <p className={styles.error}>
              {invite.error instanceof Error && invite.error.message
                ? invite.error.message
                : "Couldn’t send that invite. Check the email and try again."}
            </p>
          )}
          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.cancel}
              onClick={() => {
                setAdding(false);
                setEmail("");
                setAlias("");
              }}
            >
              Cancel
            </button>
            <button type="submit" className={styles.send} disabled={invite.isPending || !email.trim()}>
              {invite.isPending ? "Sending…" : "Send invite"}
            </button>
          </div>
        </form>
        {alreadyLinked && (
          <ActionModal
            title="Already someone’s partner"
            confirmLabel="OK"
            hideCancel
            onConfirm={() => setAlreadyLinked(null)}
            onCancel={() => setAlreadyLinked(null)}
          >
            <p className={styles.confirmText}>
              <b>{alreadyLinked}</b> is already linked as another member&rsquo;s partner. A person can be the partner
              of one member at a time.
            </p>
          </ActionModal>
        )}
      </>
    );
  }

  return (
    <div className={styles.inset}>
      <button type="button" className={styles.add} onClick={() => setAdding(true)}>
        + Add your partner
      </button>
    </div>
  );
}

/** The (…) on a joined partner's row: change what kind of partner they are, or remove them. Cancel · Remove · Save. */
function ManagePartnerModal({ link, name, onClose }: { link: PartnerLinkView; name: string; onClose: () => void }) {
  const remove = useRemovePartnerLink();
  const setRelationship = useSetPartnerRelationship();
  const [value, setValue] = useState(link.relationship ?? "");
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const unchanged = value === (link.relationship ?? "");

  // Remove asks first — the partner loses access at once. Cancel steps back to the (…) modal, not out of it.
  if (confirmingRemove) {
    return (
      <ActionModal
        title={`Remove ${name}`}
        confirmLabel="Remove"
        confirmTone="danger"
        loading={remove.isPending}
        onConfirm={() =>
          link.partnerLinkId &&
          remove.mutate(link.partnerLinkId, {
            onSuccess: onClose,
            onError: () => pushToast(`Couldn’t remove ${name}. Try again.`),
          })
        }
        onCancel={() => setConfirmingRemove(false)}
      >
        <p className={styles.confirmText}>
          Remove <b>{name}</b>? They&rsquo;ll lose access to your shows and travel.
        </p>
      </ActionModal>
    );
  }

  return (
    <ActionModal
      title={name}
      onCancel={onClose}
      actions={[
        { label: "Remove", tone: "danger", onClick: () => setConfirmingRemove(true) },
        {
          label: "Save",
          primary: true,
          disabled: unchanged,
          loading: setRelationship.isPending,
          onClick: () =>
            link.partnerLinkId &&
            setRelationship.mutate(
              { partnerLinkId: link.partnerLinkId, relationship: value || null },
              { onSuccess: onClose, onError: () => pushToast("Couldn’t save that. Try again.") },
            ),
        },
      ]}
    >
      <Picker
        label="Relationship"
        placeholder="Not set"
        value={value}
        onChange={setValue}
        options={[
          { value: "", label: "Not set" },
          ...PARTNER_RELATIONSHIPS.map((r) => ({ value: r.value, label: r.label })),
        ]}
      />
    </ActionModal>
  );
}
